import 'intersection-observer'

import { Suspense } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { Provider } from 'react-redux'
import 'lib/i18n'
import i18next from 'i18next'
import { Translation } from 'react-i18next'
import { initReactI18next } from 'react-i18next'
import * as Sentry from '@sentry/react'

// `GlobalReCaptcha` has to come before `useWebsiteInitializer`
// because the former has `useVerifyRecaptchaMutation`,
// while the latter has `useGetGroupCategoriesQuery`.
//
// If their positions are swapped, there will be a problem around
// `app/javascript/store/libs/baseQuery.js`'s
// `recaptchaApi.endpoints.verifyRecaptcha.select('single')`.
import GlobalReCaptcha from 'components/ReCaptcha/GlobalReCaptcha'

import { FeatureFlagProvider } from 'features/FeatureFlag'
import { initDayJs, switchDayJsLocale } from 'lib/time'
import { store } from 'store/config'

import Notification from 'components/Notification'
import PageLoading from 'components/Loading/PageLoading'
import GoToTop from 'components/GoToTop'
import SuggestOpenAppPopup from 'features/SuggestOpenAppPopup'
import { PushNotificationContextProvider } from 'features/PushNotification/PushNotificationContext'
import { RemoteConfig } from 'features/RemoteConfig'
import GlobalConfirmBox from 'features/GlobalConfirmBox'
import GlobalErrorPopup from 'features/GlobalErrorPopup'
import YayWebSocketContextProvider from 'features/YayWebSocket/YayWebSocketContext'
import { AppRoutes } from './AppRoutes'
import { defaultLang } from 'config'

import '../stylesheets/application.scss'

i18next.use(initReactI18next).init({
  debug: false,
  fallbackLng: 'en',
})

i18next.on('languageChanged', function (lang) {
  switchDayJsLocale(lang)
})

initDayJs(i18next.language || defaultLang)

if (process.env.SENTRY_DSN) {
  Sentry.init({
    environment: process.env.ENV ?? 'development',
    dsn: process.env.SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // Tracing
    tracesSampleRate: 0.5, //  Capture 50% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/((web-stg|web-review)\.)?yay\.space/,
    ],
    allowUrls: ['localhost', /^https:\/\/((web-stg|web-review)\.)?yay\.space/],
    // Session Replay
    replaysSessionSampleRate: 0.01, // This sets the sample rate at 1%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 0.01, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    defaultIntegrations: false,
  })
}

const App = () => {
  const isMaintenanced = window.gon.setting.is_maintenanced === '1'

  if (isMaintenanced) return null
  return (
    <Suspense fallback={<PageLoading />}>
      <BrowserRouter>
        <Provider store={store}>
          <FeatureFlagProvider>
            <PushNotificationContextProvider>
              <RemoteConfig>
                <YayWebSocketContextProvider>
                  <Translation>
                    {() => (
                      <>
                        <AppRoutes />
                        <GlobalReCaptcha />
                        <Notification />
                        <SuggestOpenAppPopup />
                        <GlobalConfirmBox />
                        <GlobalErrorPopup />
                        <GoToTop />
                      </>
                    )}
                  </Translation>
                </YayWebSocketContextProvider>
              </RemoteConfig>
            </PushNotificationContextProvider>
          </FeatureFlagProvider>
        </Provider>
      </BrowserRouter>
    </Suspense>
  )
}

export default App
